/**
 * Wire types for the `users` module.
 *
 * ---------------------------------------------------------------------------
 * FIELD-NAMING DECISION: TypeScript types keep the backend's snake_case
 * verbatim. There is no camelCase mapping layer at the API boundary.
 * ---------------------------------------------------------------------------
 *
 * Why:
 *
 * 1. snake_case is a deliberate, load-bearing part of the backend contract, not
 *    an accident. `refactor/backend/src/modules/users/serializers.ts` opens with
 *    "Output uses Django/DRF's snake_case field names verbatim so the existing
 *    Flutter client parses Node responses unchanged" — during the staged
 *    rollout the SAME endpoints serve both the Flutter app and this SPA, so the
 *    wire format cannot drift.
 *
 * 2. A camelCase mapping layer needs a hand-written (or reflective) transform
 *    for every nested shape in all 10 modules. When the map and the wire drift,
 *    the failure mode is a silent `undefined` at runtime — exactly the class of
 *    bug a type system is supposed to prevent. With snake_case types, the
 *    declared type IS the contract, so `user.firstName` is a compile error
 *    rather than a blank name in the UI.
 *
 * 3. A reflective deep-transform (e.g. camelcase-keys) is worse, not better: it
 *    would also rewrite keys that are DATA rather than field names — the M-Pesa
 *    endpoints pass Safaricom's raw JSON through verbatim (`MerchantRequestID`,
 *    `CallbackMetadata`), and `reports/dashboard/` returns objects keyed by
 *    values, not by a fixed schema.
 *
 * The cost is that snake_case leaks into component code. That is accepted:
 * it is confined to values that came off the wire, and it makes it obvious at
 * a glance which objects are server payloads. Locally-constructed frontend
 * state (props, hooks, form state) stays camelCase as normal.
 */

/** `UserRole` — rent_management_app/lib/core/constants.dart:78-81. */
export const USER_ROLES = ['LANDLORD', 'AGENT', 'TENANT', 'SUPERAGENT'] as const;

export type UserRole = (typeof USER_ROLES)[number];

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

/** `UserProfileSerializer` — backend src/modules/users/serializers.ts:73-89. */
export interface UserProfile {
  avatar: string | null;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  date_of_birth: string | null;
  emergency_contact_name: string;
  emergency_contact_phone: string;
}

/**
 * `UserSerializer` — backend src/modules/users/serializers.ts:105-139.
 *
 * `id` is a `number`, not a string: Django's `BigAutoField` PKs are rendered as
 * plain JSON numbers, and the Node port reproduces that with an app-level json
 * replacer (refactor/backend/src/app.ts:41-43, and the "BigInt primary keys are
 * serialised as JSON numbers" entry in refactor/docs/PARITY_NOTES.md).
 *
 * `current_balance` is a `number` rather than a decimal string because it is a
 * DRF `SerializerMethodField` — no `DecimalField` coercion runs, so it lands as
 * a JSON float. Money that comes off a real `DecimalField` (invoice amounts,
 * rent) arrives as a quantised STRING instead; do not assume one from the other.
 */
export interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  phone: string;
  is_verified: boolean;
  /** NEW, beyond Django parity — see PARITY_NOTES.md. Powers the admin deactivate/reactivate UI. */
  is_active: boolean;
  date_joined: string;
  profile: UserProfile | null;

  /** Derived from the user's current (non-TERMINATED) lease; null for non-tenants. */
  property_name: string | null;
  property_location: string | null;
  unit_number: string | null;
  unit_id: number | null;
  /** 'NO_ACTIVE_LEASE' when there is no current lease — never null. */
  lease_status: string;

  current_balance: number;

  /**
   * Node-only, beyond Django parity (compliance item: KRA/tax tracking —
   * see backend src/lib/kraPin.ts). Only present on `GET|PATCH auth/me/`
   * responses (the route merges it in from a separate table); ABSENT from
   * `GET auth/` (the user list) and any other endpoint that returns a
   * `User`, so treat it as optional everywhere except the profile page.
   */
  kra_pin?: string | null;
}

export function fullName(user: Pick<User, 'first_name' | 'last_name'>): string {
  return `${user.first_name} ${user.last_name}`.trim();
}
